/**
 * O que o app instalado consegue mostrar antes de falar com o servidor.
 *
 * Um PWA aberto no metrô precisa renderizar a lista de conversas e a última
 * conversa aberta em vez de uma tela branca com spinner. Isso vem da
 * persistência do React Query — **não** de cachear `/api` no service worker,
 * que devolveria um 200 de horas atrás como se fosse ao vivo. O que fica aqui
 * carrega `dataUpdatedAt`, então a tela sabe (e pode dizer) que é um retrato.
 *
 * IndexedDB e não localStorage por dois motivos: cem mensagens de várias
 * conversas estouram os ~5 MB do localStorage, e a escrita dele é síncrona na
 * thread principal — exatamente o que trava a rolagem num celular modesto.
 */
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { Query } from "@tanstack/react-query";
import { clear, del, get, set } from "idb-keyval";

const CHAVE = "t2c-query-cache";

/** Só o que serve para abrir o app offline e começar a trabalhar.
 *
 *  Deliberadamente curto: cada chave aqui é dado de atendimento gravado no
 *  disco do aparelho. Listagem de configuração (tags, squads, macros) não
 *  entra — reaparece sozinha assim que há rede e não vale o risco. */
const PREFIXOS_PERSISTIDOS: readonly (readonly unknown[])[] = [
  ["helpdesk", "threads", "desk"],
  ["helpdesk", "threads", "desk-counts"],
  ["helpdesk", "thread"],
  ["helpdesk", "messages"],
  ["notifications", "unread-count"],
];

function combina(chave: readonly unknown[], prefixo: readonly unknown[]): boolean {
  if (chave.length < prefixo.length) return false;
  return prefixo.every((parte, i) => chave[i] === parte);
}

export function deveGuardar(query: Query): boolean {
  // Query com erro persistida volta como erro no próximo lançamento, e a tela
  // mostraria falha antes mesmo de tentar a rede.
  if (query.state.status !== "success") return false;
  return PREFIXOS_PERSISTIDOS.some((p) => combina(query.queryKey, p));
}

export const persister = createAsyncStoragePersister({
  storage: {
    getItem: (chave) => get<string>(chave).then((v) => v ?? null),
    setItem: (chave, valor) => set(chave, valor),
    removeItem: (chave) => del(chave),
  },
  key: CHAVE,
  // A escrita é jogada para fora do caminho de renderização; 1 s agrupa a
  // rajada de queries que uma troca de conversa dispara.
  throttleTime: 1000,
});

/**
 * Apaga o retrato gravado no aparelho.
 *
 * Obrigatório no logout e na troca de empresa: sem isto, a próxima pessoa a
 * abrir o app neste celular — ou a mesma pessoa noutra empresa — veria as
 * conversas anteriores renderizadas a partir do disco antes de qualquer
 * chamada ao servidor. `queryClient.clear()` sozinho não basta, porque ele
 * limpa a memória e não o IndexedDB.
 */
export async function limparCachePersistido(): Promise<void> {
  try {
    await del(CHAVE);
  } catch {
    // Sem IndexedDB (janela anônima, armazenamento bloqueado) não há o que
    // limpar — e falhar aqui não pode impedir o logout.
    try {
      await clear();
    } catch {
      /* desiste em silêncio, de propósito */
    }
  }
}
